/**
 * Round numbers for a value axis.
 *
 * A bar chart's tallest bar is its own hundred per cent, which says nothing about
 * how much it is: without an axis the reader has to be told the peak in words, and
 * a scale that moves with the data cannot be compared with the card beside it.
 * This is the arithmetic behind writing the scale down the side — ones, twos and
 * fives of a power of ten, which are the numbers people read without working them
 * out, rather than 0…469 by 93.8.
 */

/** A value axis: its top, and the round values to label it with. */
export interface ValueScale {
  /** The axis's top, at or above the largest value in the data. */
  max: number;
  /** The values to write down the side, ascending, starting at zero. */
  ticks: number[];
}

/**
 * Picks round ticks for a series.
 *
 * The raw step is rounded *up* to the next one, two or five times a power of ten,
 * so the axis top is a round number at or above the data and the tallest bar sits
 * inside the grid rather than against its ceiling.
 *
 * @param rawMax The largest value the axis has to fit, in the caller's units.
 * @param intervals How many steps to aim for; the axis uses that many or one more.
 * @returns The axis top and its tick values.
 */
export function niceScale(rawMax: number, intervals = 5): ValueScale {
  // Nothing to scale. One line at zero is the whole truth about a period with no
  // sales in it, and a five-step axis of noughts would be five ways of saying it.
  if (!(rawMax > 0)) return { max: 1, ticks: [0] };

  const rough = rawMax / intervals;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const normalised = rough / magnitude;
  const nice = (normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10) * magnitude;
  // Never finer than the unit itself: a step of a fifth of a cent prints a tick
  // that no currency has, and rounding to the unit is what the reader expects of
  // the smallest axis a shop can have.
  const step = rawMax >= 1 ? Math.max(nice, 1) : nice;
  const max = Math.ceil(rawMax / step) * step;

  const ticks: number[] = [];
  const steps = Math.round(max / step);
  for (let index = 0; index <= steps; index += 1) {
    // `toPrecision` undoes the float noise a step of a tenth carries: 0.30000000000000004
    // is the same tick as 0.3 and reads as a different one.
    ticks.push(Number((index * step).toPrecision(12)));
  }

  return { max, ticks };
}
